<template>
  <profile-edit-section-card
    v-if="localState"
    title="Special needs"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <radio-button-group
      v-model="specialNeedsValue"
      name="Do you have special needs experience?"
      :options="yesNoOptions"
      class="mb-24"
    />
    <template v-if="localState.specialNeeds">
      <radio-button-group
        v-model="childWithSpecialNeedsValue"
        name="Do you have a child of your own with a special need?"
        :options="yesNoOptions"
        class="mb-24"
      />
      <poppins-dropdown
        id="special-needs-years"
        v-model="localState.specialNeedsYearsExperience"
        class="mb-24"
        label-name="Years of special needs experience"
        :options="yearsOptions"
      />
      <fieldset class="mb-24">
        <legend class="form__label !mb-0">Age groups</legend>
        <helper-text text="Select all that apply" class="pb-16 pl-0" />
        <ul>
          <li v-for="option in ageGroupOptions" :key="option.value" class="mb-8">
            <poppins-checkbox
              v-model="localState.specialNeedsRecipientAgeGroups"
              :label-name="option.label"
              :value="option.value"
            />
          </li>
        </ul>
      </fieldset>
      <fieldset class="mb-24">
        <legend class="form__label !mb-0">Care experience</legend>
        <helper-text text="Select all that apply" class="pb-16 pl-0" />
        <ul>
          <li v-for="option in careOptions" :key="option.value" class="mb-8">
            <poppins-checkbox v-model="localState.specialNeedsCareExperience" :label-name="option.label" :value="option.value" />
          </li>
        </ul>
      </fieldset>
      <fieldset class="mb-24">
        <legend class="form__label !mb-0">Conditions</legend>
        <helper-text text="Select all that apply" class="pb-16 pl-0" />
        <ul>
          <li v-for="option in conditionOptions" :key="option.value" class="mb-8">
            <poppins-checkbox
              v-model="localState.specialNeedsConditionExperience"
              :label-name="option.label"
              :value="option.value"
            />
          </li>
        </ul>
      </fieldset>
      <fieldset>
        <legend class="form__label !mb-0">Services</legend>
        <helper-text text="Select all that apply" class="pb-16 pl-0" />
        <ul>
          <li v-for="option in serviceOptions" :key="option.value" class="mb-8">
            <poppins-checkbox
              v-model="localState.specialNeedsServiceExperience"
              :label-name="option.label"
              :value="option.value"
            />
          </li>
        </ul>
      </fieldset>
    </template>
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import PoppinsDropdown from '@/components/dropdown/Dropdown.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import RadioButtonGroup from '@/components/radioButtonGroup/RadioButtonGroup.vue';
import HelperText from '@/components/helper_text/HelperText.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import {
  SPECIAL_NEEDS_AGE_GROUP_OPTIONS,
  SPECIAL_NEEDS_CARE_OPTIONS,
  SPECIAL_NEEDS_CONDITION_OPTIONS,
  SPECIAL_NEEDS_SERVICE_OPTIONS,
  SPECIAL_NEEDS_YEARS_OPTIONS,
} from '../../__types__/CareTypeFormOptions';
import type { ChildCareProfileFormState } from '../../__types__/ChildCareProfileTypes';

export default {
  name: 'ChildCareSpecialNeedsForm',
  components: {
    ProfileEditSectionCard,
    RadioButtonGroup,
    PoppinsCheckbox,
    PoppinsDropdown,
    HelperText,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
    };
  },
  computed: {
    yesNoOptions: () => [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    yearsOptions: () => SPECIAL_NEEDS_YEARS_OPTIONS,
    ageGroupOptions: () => SPECIAL_NEEDS_AGE_GROUP_OPTIONS,
    careOptions: () => SPECIAL_NEEDS_CARE_OPTIONS,
    conditionOptions: () => SPECIAL_NEEDS_CONDITION_OPTIONS,
    serviceOptions: () => SPECIAL_NEEDS_SERVICE_OPTIONS,
    specialNeedsValue: {
      get(): string {
        return String(this.localState?.specialNeeds ?? false);
      },
      set(value: string) {
        if (this.localState) this.localState.specialNeeds = value === 'true';
      },
    },
    childWithSpecialNeedsValue: {
      get(): string {
        return String(this.localState?.childWithSpecialNeeds ?? false);
      },
      set(value: string) {
        if (this.localState) this.localState.childWithSpecialNeeds = value === 'true';
      },
    },
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    initializeFormState() {
      const state = this.formState as ChildCareProfileFormState | null;
      if (!state) return;

      this.localState = {
        specialNeeds: state.specialNeeds ?? false,
        childWithSpecialNeeds: state.childWithSpecialNeeds ?? false,
        specialNeedsYearsExperience: state.specialNeedsYearsExperience || '',
        specialNeedsRecipientAgeGroups: [...(state.specialNeedsRecipientAgeGroups || [])],
        specialNeedsCareExperience: [...(state.specialNeedsCareExperience || [])],
        specialNeedsConditionExperience: [...(state.specialNeedsConditionExperience || [])],
        specialNeedsServiceExperience: [...(state.specialNeedsServiceExperience || [])],
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    async handleSave() {
      if (!this.localState || !this.hasUnsavedChanges) return;

      const hasExperience = this.localState.specialNeeds;
      await this.saveSection({
        specialNeeds: hasExperience,
        childWithSpecialNeeds: hasExperience ? this.localState.childWithSpecialNeeds : false,
        specialNeedsYearsExperience: hasExperience ? this.localState.specialNeedsYearsExperience || null : null,
        specialNeedsRecipientAgeGroups: hasExperience ? [...this.localState.specialNeedsRecipientAgeGroups] : [],
        specialNeedsCareExperience: hasExperience ? [...this.localState.specialNeedsCareExperience] : [],
        specialNeedsConditionExperience: hasExperience ? [...this.localState.specialNeedsConditionExperience] : [],
        specialNeedsServiceExperience: hasExperience ? [...this.localState.specialNeedsServiceExperience] : [],
      });
    },
  },
};
</script>
